<template>
  <div class="match-page">
    <div v-if="loading" class="state-box">
      <div class="spinner"></div>
      <p>در حال دریافت اطلاعات بازی...</p>
    </div>

    <div v-else-if="error" class="state-box error">
      <i class="fas fa-exclamation-circle"></i>
      <p>{{ error }}</p>
      <router-link to="/" class="back-btn">بازگشت به خانه</router-link>
    </div>

    <div v-else-if="match" class="match-container">
      <!-- breadcrumb -->
      <div class="breadcrumb">
        <router-link to="/">خانه</router-link>
        <i class="fas fa-chevron-left"></i>
        <router-link v-if="match.competition" :to="`/competition/${match.competition.id}`">
          {{ leagueName(match.competition) }}
        </router-link>
        <i class="fas fa-chevron-left"></i>
        <span>{{ teamName(homeTeam.name) }} - {{ teamName(awayTeam.name) }}</span>
      </div>

      <!-- ===== هدر بازی ===== -->
      <div class="match-hero">
        <div class="match-hero-meta">
          <div class="meta-competition">
            <img v-if="match.competition.logo" :src="match.competition.logo" class="competition-logo"/>
            <span>{{ leagueName(match.competition) }}</span>
            <span v-if="roundLabel(match.round)" class="meta-round">· {{ roundLabel(match.round) }}</span>
          </div>
          <div class="meta-right">
            <span class="meta-date">{{ formattedDate }}</span>
            <span v-if="match.venue" class="meta-venue">
              <i class="fas fa-map-marker-alt"></i> {{ match.venue.name }}
            </span>
          </div>
        </div>

        <div class="match-hero-teams">
          <!-- تیم خانه -->
          <div class="hero-team" @click="goToTeam(match.home.team)">
            <img v-if="match.home.team.logo" :src="match.home.team.logo" class="hero-team-logo"/>
            <span class="hero-team-name">{{ teamName(match.home.team.name) }}</span>
          </div>

          <!-- نتیجه -->
          <div class="hero-score">
            <template v-if="isFinished">
              <div class="score-display">
                <span class="score-num" :class="{ winner: match.home.isWinner }">{{ match.home.score }}</span>
                <span class="score-sep">-</span>
                <span class="score-num" :class="{ winner: match.away.isWinner }">{{ match.away.score }}</span>
              </div>
            </template>
            <template v-else-if="isLive">
              <div class="score-display live">
                <span class="score-num">{{ match.home.score }}</span>
                <span class="score-sep">-</span>
                <span class="score-num">{{ match.away.score }}</span>
              </div>
            </template>
            <template v-else>
              <div class="score-time">{{ formattedTime }}</div>
            </template>
            <span :class="['status-badge', statusClass]">{{ statusLabel }}</span>
          </div>

          <!-- تیم میهمان -->
          <div class="hero-team" @click="goToTeam(match.away.team)">
            <img v-if="match.away.team.logo" :src="match.away.team.logo" class="hero-team-logo"/>
            <span class="hero-team-name">{{ teamName(match.away.team.name) }}</span>
          </div>
        </div>
      </div>

      <!-- ===== رویدادها ===== -->
      <div class="section-box">
        <div class="section-title"><i class="fas fa-futbol"></i> رویدادها</div>
        <div class="tab-content">
          <div v-if="match.events.length === 0" class="empty-state">
            <i class="fas fa-futbol"></i>
            <p>رویدادی ثبت نشده</p>
          </div>
          <div v-else class="events-timeline">
            <div
                v-for="event in match.events"
                :key="event.id"
                :class="['event-row', event.team?.id === match.home.team.id ? 'event-home' : 'event-away']"
            >
              <div class="event-home-side">
                <template v-if="event.team?.id === match.home.team.id">
                  <span class="event-player">{{ event.player?.name }}</span>
                  <span v-if="event.relatedPlayer" class="event-related">↑ {{ event.relatedPlayer.name }}</span>
                  <i :class="['event-icon', eventIcon(event.type)]"></i>
                </template>
              </div>
              <div class="event-minute">{{ event.minute }}<span v-if="event.extraMinute">+{{ event.extraMinute }}</span>'
              </div>
              <div class="event-away-side">
                <template v-if="event.team?.id === match.away.team.id">
                  <i :class="['event-icon', eventIcon(event.type)]"></i>
                  <span class="event-player">{{ event.player?.name }}</span>
                  <span v-if="event.relatedPlayer" class="event-related">↑ {{ event.relatedPlayer.name }}</span>
                </template>
              </div>
            </div>
          </div>
        </div>

      </div>
      <!-- ===== ترکیب ===== -->
      <div class="section-box">
        <div class="section-title">
          <span><i class="fas fa-users"></i> ترکیب تیم‌ها</span>
          <i :class="['fas', lineupOpen ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
        </div>
        <div  class="tab-content">
          <div v-if="match.lineups.length === 0" class="empty-state">
            <i class="fas fa-users"></i>
            <p>ترکیب اعلام نشده</p>
          </div>
          <div v-else class="lineups-grid">
            <div v-for="lineup in match.lineups" :key="lineup.team.id" class="lineup-col">
              <div class="lineup-header">
                <img v-if="lineup.team.logo" :src="lineup.team.logo" class="lineup-logo"/>
                <div>
                  <div class="lineup-team-name">{{ teamName(lineup.team.name) }}</div>
                  <div v-if="lineup.formation" class="lineup-formation">{{ lineup.formation }}</div>
                  <div v-if="lineup.coach" class="lineup-coach">
                    <i class="fas fa-user-tie"></i> {{ lineup.coach.name }}
                  </div>
                </div>
              </div>

              <div class="lineup-section">
                <h4>ترکیب اصلی</h4>
                <div
                    v-for="player in lineup.starters"
                    :key="player.id"
                    class="player-row"
                    @click="$router.push(`/player/${player.slug}`)"
                >
                  <span class="player-number">{{ player.shirtNumber }}</span>
                  <span class="player-name">{{ player.name }}</span>
                  <span class="player-pos">{{ posLabel(player.position) }}</span>
                </div>
              </div>

              <div class="lineup-section" v-if="lineup.bench.length">
                <h4>ذخیره‌ها</h4>
                <div
                    v-for="player in lineup.bench"
                    :key="player.id"
                    class="player-row bench"
                    @click="$router.push(`/player/${player.slug}`)"
                >
                  <span class="player-number">{{ player.shirtNumber }}</span>
                  <span class="player-name">{{ player.name }}</span>
                  <span class="player-pos">{{ posLabel(player.position) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
      <!-- ===== آمار و رو در رو ===== -->
      <div class="section-box" v-if="hasStatsSection">
        <div class="section-title"><i class="fas fa-chart-bar"></i> آمار و رو در رو</div>
        <div class="tab-content">

          <!-- آمار بازی (از رویدادها) -->
          <div v-if="hasMatchStats" class="match-stats">
            <div
                v-if="matchMotm"
                class="motm-card"
                @click="goPlayerPage(matchMotm.player)"
            >
              <span class="motm-badge"><i class="fas fa-star"></i> بهترین بازیکن زمین</span>
              <div class="motm-player">
                <span class="motm-avatar" v-if="matchMotm.player.photo"><img :src="matchMotm.player.photo" :alt="matchMotm.player.name"/></span>
                <span v-else class="motm-avatar initials">{{ initialsOf(matchMotm.player.name) }}</span>
                <div class="motm-meta">
                  <span class="motm-name">{{ matchMotm.player.name }}</span>
                  <small class="motm-sub">
                    <template v-if="matchMotm.goals">{{ faNum(matchMotm.goals) }} گل</template>
                    <template v-if="matchMotm.goals && matchMotm.assists"> + </template>
                    <template v-if="matchMotm.assists">{{ faNum(matchMotm.assists) }} پاس گل</template>
                  </small>
                </div>
              </div>
              <small class="motm-note">بر اساس رویدادهای ثبت‌شده</small>
            </div>

            <div class="ms-bars">
              <div v-for="row in matchStatRows" :key="row.key" class="ms-row">
                <div class="ms-val">{{ faNum(row.a) }}</div>
                <div class="ms-mid">
                  <span class="ms-label">{{ row.label }}</span>
                  <div class="ms-track">
                    <div class="ms-half home">
                      <div class="ms-fill home" :style="{ width: row.aPct + '%' }"></div>
                    </div>
                    <div class="ms-half away">
                      <div class="ms-fill away" :style="{ width: row.bPct + '%' }"></div>
                    </div>
                  </div>
                </div>
                <div class="ms-val">{{ faNum(row.b) }}</div>
              </div>
            </div>
          </div>

          <!-- کارت دو تیم: جایگاه + فرم -->
          <div class="teams-overview">
            <div
                v-for="side in ['home', 'away']"
                :key="side"
                class="team-overview"
            >
              <div class="overview-head">
                <img
                    v-if="sideTeam(side).logo"
                    :src="sideTeam(side).logo"
                    class="overview-logo"
                />
                <div class="overview-titles">
                  <span class="overview-name">{{ teamName(sideTeam(side).name) }}</span>
                  <span
                      v-if="match.standings?.[side]"
                      class="overview-rank"
                  >
                    رتبه {{ faNum(match.standings[side].position) }} ·
                    {{ faNum(match.standings[side].points) }} امتیاز
                  </span>
                </div>
              </div>

              <div
                  v-if="match.form?.[side]?.length"
                  class="form-row"
              >
                <span class="form-label">فرم اخیر:</span>
                <span
                    v-for="(f, i) in match.form[side]"
                    :key="i"
                    :class="['form-badge', formClass(f.result)]"
                    :title="`${teamName(f.opponent?.name)} (${f.score})`"
                >{{ formLabel(f.result) }}</span>
              </div>

              <table
                  v-if="match.standings?.[side]"
                  class="overview-table"
              >
                <tbody>
                <tr><td>بازی</td><td>{{ faNum(match.standings[side].played) }}</td></tr>
                <tr><td>برد / مساوی / باخت</td>
                  <td>{{ faNum(match.standings[side].won) }} / {{ faNum(match.standings[side].draw) }} / {{ faNum(match.standings[side].lost) }}</td></tr>
                <tr><td>گل زده / خورده</td>
                  <td>{{ faNum(match.standings[side].goalsFor) }} / {{ faNum(match.standings[side].goalsAgainst) }}</td></tr>
                <tr><td>تفاضل</td>
                  <td>{{ faNum(match.standings[side].goalDifference) }}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- رویارویی‌های مستقیم -->
          <div
              v-if="match.h2h && match.h2h.length"
              class="h2h-box"
          >
            <h4 class="h2h-title"><i class="fas fa-exchange-alt"></i> رویارویی‌های مستقیم</h4>
            <div
                v-for="m in match.h2h"
                :key="m.id"
                class="h2h-row"
                @click="$router.push(`/match/${m.id}`)"
            >
              <div class="h2h-team" @click.stop="$router.push(`/team/${m.home?.id}`)">
                <img
                    v-if="m.home?.logo"
                    :src="m.home.logo"
                >
                <span :class="{ winner: m.home?.isWinner }">{{ teamName(m.home?.name) }}</span>
              </div>
              <div class="h2h-center">
                <strong>{{ m.home?.score ?? 0 }} - {{ m.away?.score ?? 0 }}</strong>
                <small>{{ h2hDate(m.kickoffAt) }}</small>
              </div>
              <div class="h2h-team away" @click.stop="$router.push(`/team/${m.away?.id}`)">
                <span :class="{ winner: m.away?.isWinner }">{{ teamName(m.away?.name) }}</span>
                <img
                    v-if="m.away?.logo"
                    :src="m.away.logo"
                >
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- ===== اخبار مرتبط ===== -->
      <div class="section-box">
        <div class="section-title"><i class="fas fa-newspaper"></i> اخبار مرتبط</div>
        <div class="tab-content">
          <div v-if="match.news.length === 0" class="empty-state">
            <i class="fas fa-newspaper"></i>
            <p>خبری پیدا نشد</p>
          </div>
          <div v-else class="news-grid">
            <article
                v-for="item in match.news"
                :key="item.id"
                class="news-card"
                @click="$router.push({ name: 'NewsDetail', params: { slug: item.id + '-' + slugify(item.title) } })"
            >
              <div class="news-img">
                <img v-if="item.imageUrl" :src="item.imageUrl" :alt="item.title"
                     @error="e => e.target.style.display='none'"/>
                <div v-else class="news-no-img"><i class="fas fa-newspaper"></i></div>
                <span v-if="item.source" class="news-source-badge">{{ item.source.name }}</span>
              </div>
              <div class="news-body">
                <h4>{{ item.title }}</h4>
                <span class="news-time">{{ timeAgo(item.publishedAt) }}</span>
              </div>
            </article>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api'
import { teamNames, leagueNamesLatin, roundLabel as roundLabelFa } from '@/utils/translations'
import { setSeo } from '@/utils/seo'

export default {
  name: 'MatchDetail',
  components: {},

  data() {
    return {
      match: null,
      loading: true,
      error: null,
      lineupOpen: false,
    }
  },

  computed: {
    homeTeam() {
      return this.match?.home?.team || {}
    },
    awayTeam() {
      return this.match?.away?.team || {}
    },

    isLive() {
      return ['LIVE', 'IN_PLAY', 'PAUSED'].includes(this.match?.status)
    },
    isFinished() {
      return this.match?.status === 'FINISHED'
    },

    statusLabel() {
      const map = {
        SCHEDULED: 'برنامه‌ریزی شده',
        TIMED: 'برنامه‌ریزی شده',
        LIVE: '🔴 زنده',
        IN_PLAY: '🔴 در جریان',
        PAUSED: '⏸ نیمه استراحت',
        FINISHED: 'پایان یافته',
        POSTPONED: 'به تعویق افتاده',
        CANCELLED: 'لغو شده',
        SUSPENDED: 'متوقف شده',
      }
      return map[this.match?.status] || this.match?.status
    },

    statusClass() {
      if (this.isLive) return 'live'
      if (this.isFinished) return 'finished'
      return 'upcoming'
    },

    formattedDate() {
      if (!this.match?.kickoffAt) return ''
      return new Date(this.match.kickoffAt).toLocaleDateString('fa-IR', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
      })
    },

    formattedTime() {
      if (!this.match?.kickoffAt) return ''
      return new Date(this.match.kickoffAt).toLocaleTimeString('fa-IR', {
        hour: '2-digit', minute: '2-digit'
      })
    },

    // آیا سکشن آمار چیزی برای نشان دادن دارد؟
    hasStatsSection() {
      const m = this.match
      if (!m) return false
      return (m.h2h && m.h2h.length > 0) ||
          (m.form?.home?.length > 0) ||
          (m.form?.away?.length > 0) ||
          !!(m.standings?.home || m.standings?.away)
    },

    // ─── آمار بازی از رویدادها ───
    sideCounts() {
      const m = this.match
      if (!m?.events?.length || !m.home?.team) return null
      const homeId = m.home.team.id
      const awayId = m.away?.team?.id
      if (awayId == null) return null
      const empty = () => ({ goals: 0, penaltyGoals: 0, ownGoals: 0, missedPenalties: 0, yellowCards: 0, redCards: 0, secondYellows: 0, substitutions: 0 })
      const out = { home: empty(), away: empty() }
      const sideOf = t => (t === homeId ? 'home' : (t === awayId ? 'away' : null))
      for (const e of m.events) {
        const s = sideOf(e.team?.id)
        if (!s) continue
        if (e.type === 'GOAL' || e.type === 'PENALTY_GOAL') out[s].goals++
        if (e.type === 'PENALTY_GOAL') out[s].penaltyGoals++
        if (e.type === 'OWN_GOAL') out[s].ownGoals++
        if (e.type === 'MISSED_PENALTY') out[s].missedPenalties++
        if (e.type === 'YELLOW_CARD') out[s].yellowCards++
        if (e.type === 'RED_CARD') out[s].redCards++
        if (e.type === 'SECOND_YELLOW') out[s].secondYellows++
        if (e.type === 'SUBSTITUTION') out[s].substitutions++
      }
      return out
    },

    matchStatRows() {
      const c = this.sideCounts
      if (!c) return []
      const def = [
        { key: 'goals', label: 'گل' },
        { key: 'penaltyGoals', label: 'پنالتی', opt: true },
        { key: 'ownGoals', label: 'گل به خودی', opt: true },
        { key: 'missedPenalties', label: 'پنالتی از دست رفته', opt: true },
        { key: 'yellowCards', label: 'کارت زرد' },
        { key: 'redCards2', label: 'کارت قرمز', opt: true },
        { key: 'substitutions', label: 'تعویض', opt: true },
      ]
      const rows = []
      for (const d of def) {
        let a, b
        if (d.key === 'redCards2') {
          a = c.home.redCards + c.home.secondYellows
          b = c.away.redCards + c.away.secondYellows
        } else {
          a = c.home[d.key]
          b = c.away[d.key]
        }
        if (d.opt && !a && !b) continue
        if (!d.opt && !a && !b) continue
        const max = Math.max(a, b, 1)
        rows.push({
          key: d.key,
          label: d.label,
          a, b,
          aPct: Math.round((a / max) * 100),
          bPct: Math.round((b / max) * 100),
        })
      }
      return rows
    },

    hasMatchStats() {
      return this.sideCounts && this.matchStatRows.length > 0
    },

    // بهترین بازیکن زمین — هیوریستیک روی وزن رویدادها
    matchMotm() {
      const m = this.match
      if (!m?.events?.length) return null
      const WEIGHTS = {
        GOAL: 3, PENALTY_GOAL: 3, OWN_GOAL: -1, MISSED_PENALTY: -0.5,
        YELLOW_CARD: -0.3, RED_CARD: -1, SECOND_YELLOW: -1,
      }
      const byPlayer = new Map()
      const touch = (p, fn) => {
        if (!p || p.id == null) return
        let cur = byPlayer.get(p.id)
        if (!cur) { cur = { score: 0, goals: 0, assists: 0, player: p }; byPlayer.set(p.id, cur) }
        fn(cur)
      }
      for (const e of m.events) {
        const w = WEIGHTS[e.type]
        if (w != null) {
          touch(e.player, cur => {
            cur.score += w
            if (e.type === 'GOAL' || e.type === 'PENALTY_GOAL') cur.goals++
          })
        }
        if (e.type === 'GOAL' || e.type === 'PENALTY_GOAL') {
          touch(e.relatedPlayer, cur => { cur.score += 2; cur.assists++ })
        }
      }
      let best = null
      for (const p of byPlayer.values()) {
        if (p.score < 2) continue
        if (!best || p.score > best.score || (p.score === best.score && p.goals > best.goals)) best = p
      }
      return best ? { player: best.player, goals: best.goals, assists: best.assists } : null
    },


  },

  methods: {
    async loadMatch() {
      this.loading = true
      this.error = null
      try {
        const id = this.$route.params.id
        this.match = await apiService.requestWithNotFoundRetry('/matches/' + id)
        if (!this.match) {
          setSeo({ notFound: apiService.lastStatus === 404, title: 'بازی پیدا نشد | ضربه آزاد', path: `/match/${id}` })
          this.error = 'بازی پیدا نشد'
        }
        const h = this.match?.home?.team, a2 = this.match?.away?.team
        if (h && a2) {
          const hasScore = this.match?.home?.score != null && this.match?.away?.score != null
          setSeo({
            title: `${this.teamName(h.name)} ${hasScore ? this.match.home.score + ' - ' + this.match.away.score : 'مقابل'} ${this.teamName(a2.name)} | ضربه آزاد`,
            description: `${this.teamName(h.name)} مقابل ${this.teamName(a2.name)} — نتیجه، آمار و رویدادهای بازی در ضربه آزاد`,
            image: h.logo || a2.logo || null,
            path: `/match/${id}`,
          })
        }
        // اگه ترکیب داشت آکاردئون رو باز کن
        if (this.match?.lineups?.length > 0) {
          this.lineupOpen = true
        }
      } catch (e) {
        // فقط 404 واقعی noindex می‌گیرد؛ خطای سرور/شبکه URL سالم را noindex نمی‌کند
        setSeo({ notFound: apiService.lastStatus === 404, title: 'خطا در نمایش بازی | ضربه آزاد', path: `/match/${this.$route.params.id}` })
        this.error = 'خطا در دریافت اطلاعات بازی'
      } finally {
        this.loading = false
      }
    },

    goToTeam(team) {
      if (team?.slug) this.$router.push(`/team/${team.slug}`)
    },

    goPlayerPage(player) {
      if (player?.slug) this.$router.push(`/player/${player.slug}`)
    },

    initialsOf(name) {
      return (name || '؟').trim().slice(0, 2)
    },

    eventIcon(type) {
      const map = {
        GOAL: 'fas fa-futbol event-goal',
        OWN_GOAL: 'fas fa-futbol event-own-goal',
        PENALTY_GOAL: 'fas fa-futbol event-penalty',
        MISSED_PENALTY: 'fas fa-times-circle event-missed',
        YELLOW_CARD: 'fas fa-square event-yellow',
        RED_CARD: 'fas fa-square event-red',
        SECOND_YELLOW: 'fas fa-square event-second-yellow',
        SUBSTITUTION: 'fas fa-exchange-alt event-sub',
        VAR: 'fas fa-video event-var',
      }
      return map[type] || 'fas fa-circle event-other'
    },

    posLabel(pos) {
      const map = {
        GOALKEEPER: 'GK',
        DEFENDER: 'DEF',
        MIDFIELDER: 'MID',
        FORWARD: 'FWD',
      }
      return map[pos] || pos
    },

    slugify(title) {
      if (!title) return ''
      return title
          .trim()
          .replace(/\u200c|\u200b|\u200d|\ufeff/g, '-')
          .replace(/[,;:.!?"'()،؛«»؟!٪.:]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '')
    },

    timeAgo(dateStr) {
      if (!dateStr) return ''
      const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000)
      if (diff < 3600) return `${Math.floor(diff / 60)} دقیقه پیش`
      if (diff < 86400) return `${Math.floor(diff / 3600)} ساعت پیش`
      return `${Math.floor(diff / 86400)} روز پیش`
    },

    sideTeam(side) {
      return this.match?.[side]?.team || {}
    },

    // نام فارسی تیم (با برگشت به نام انگلیسی)
    teamName(name) {
      return teamNames[name] || name || 'نامشخص'
    },

    // نام فارسی لیگ
    leagueName(comp) {
      if (!comp) return ''
      return leagueNamesLatin[comp.name] || leagueNamesLatin[comp.shortName] || comp.name
    },

    // برچسب فارسی هفته/مرحله — خروجی خالی یعنی نمایش نده
    roundLabel(value) {
      return roundLabelFa(value)
    },

    formClass(result) {
      return { W: 'win', D: 'draw', L: 'loss' }[result] || 'draw'
    },

    formLabel(result) {
      return { W: 'برد', D: 'مساوی', L: 'باخت' }[result] || '—'
    },

    // تبدیل عدد لاتین به فارسی
    faNum(value) {
      if (value === null || value === undefined) return '—'
      return String(value).replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d])
    },

    h2hDate(dateStr) {
      if (!dateStr) return ''
      try {
        return new Intl.DateTimeFormat(
            'fa-IR-u-ca-persian',
            { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'Asia/Tehran' }
        ).format(new Date(dateStr))
      } catch (e) {
        return ''
      }
    },
  },

  mounted() {
    this.loadMatch()
  },

  watch: {
    '$route.params.id'() {
      this.loadMatch()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/sass/variables';
@import '@/assets/sass/mixin';

.match-page {
  width: 100%;
  min-height: 100vh;
  background: $gray-100;
  direction: rtl;
  font-family: $font-family;
}

.match-container {
  margin: 0 auto;
  padding: 24px 24px 60px;
}

// breadcrumb
.breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: $gray-500;
  margin-bottom: 14px;
  flex-wrap: wrap;

  a {
    color: $secondary-blue;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  i {
    font-size: 0.6rem;
  }
}

// ===== hero =====
.match-hero {
  background: 
    url('~@/assets/images/header-ball-bg.svg') no-repeat left 4% center / 260px auto,
    linear-gradient(135deg, #022c22 0%, #064e3b 45%, #047857 85%, #059669 100%);
  border-radius: 14px;
  padding: 20px;
  color: #fff;
  margin-bottom: 16px;
  position: relative;
  overflow: hidden;

  @include mobile {
    background-position: left -30px center;
    background-size: 180px auto;
  }
}

.match-hero-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.7);
  flex-wrap: wrap;
  gap: 8px;
}

.meta-competition {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: #fff;
}

.competition-logo {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.meta-round {
  opacity: 0.7;
  font-weight: 400;
}

.meta-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.meta-venue {
  font-size: 0.72rem;
}

.match-hero-teams {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.hero-team {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  text-align: center;

  &:hover .hero-team-logo {
    transform: scale(1.05);
  }
}

.hero-team-logo {
  width: 70px;
  height: 70px;
  object-fit: contain;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3));
  transition: transform 0.2s;
  @include mobile {
    width: 48px;
    height: 48px;
  }
}

.hero-team-name {
  font-size: 1rem;
  font-weight: 700;
  @include mobile {
    font-size: 0.82rem;
  }
}

.hero-score {
  text-align: center;
  min-width: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.score-display {
  display: flex;
  align-items: center;
  gap: 8px;

  &.live .score-num {
    color: #fbbf24;
  }
}

.score-num {
  font-size: 2.5rem;
  font-weight: 900;
  line-height: 1;

  &.winner {
    color: #4ade80;
  }

  @include mobile {
    font-size: 1.8rem;
  }
}

.score-sep {
  font-size: 2rem;
  opacity: 0.4;
}

.score-time {
  font-size: 1.8rem;
  font-weight: 900;
  color: #fbbf24;
  @include mobile {
    font-size: 1.4rem;
  }
}

.status-badge {
  font-size: 0.72rem;
  padding: 3px 12px;
  border-radius: 12px;
  font-weight: 600;

  &.live {
    background: #dc2626;
    color: #fff;
  }

  &.finished {
    background: rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.8);
  }

  &.upcoming {
    background: rgba(255, 255, 255, 0.1);
    color: #93c5fd;
  }
}

// ===== sections =====
.section-box {
  background: #fff;
  border-radius: 12px;
  margin-bottom: 14px;
  box-shadow: $shadow-sm;
  overflow: hidden;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  font-size: 0.95rem;
  font-weight: 700;
  color: $gray-800;
  border-bottom: 1px solid $gray-100;

  i {
    color: $primary-blue;
  }
}

.accordion-header {
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
  transition: background 0.2s;

  &:hover {
    background: $gray-50;
  }

  .fa-chevron-up, .fa-chevron-down {
    color: $gray-400;
    font-size: 0.85rem;
  }
}

// ===== محتوای تب =====
.tab-content {
  padding: 16px;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: $gray-400;

  i {
    font-size: 2.5rem;
    display: block;
    margin-bottom: 10px;
  }

  p {
    font-size: 0.9rem;
  }
}

// ===== رویدادها =====
.events-timeline {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.event-row {
  display: grid;
  grid-template-columns: 1fr 60px 1fr;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid $gray-100;

  &:last-child {
    border-bottom: none;
  }
}

.event-home-side {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  text-align: left;
}

.event-away-side {
  display: flex;
  align-items: center;
  gap: 6px;
}

.event-minute {
  text-align: center;
  font-size: 0.78rem;
  font-weight: 700;
  color: $gray-500;
  background: $gray-100;
  border-radius: 6px;
  padding: 3px 6px;
}

.event-player {
  font-size: 0.82rem;
  font-weight: 600;
  color: $gray-800;
}

.event-related {
  font-size: 0.72rem;
  color: $gray-400;
}

.event-icon {
  font-size: 0.9rem;

  &.event-goal {
    color: #16a34a;
  }

  &.event-own-goal {
    color: #dc2626;
  }

  &.event-penalty {
    color: #16a34a;
  }

  &.event-missed {
    color: #dc2626;
  }

  &.event-yellow {
    color: #eab308;
  }

  &.event-red {
    color: #dc2626;
  }

  &.event-second-yellow {
    color: #f97316;
  }

  &.event-sub {
    color: $secondary-blue;
  }

  &.event-var {
    color: $gray-500;
  }
}

// ===== ترکیب =====
.lineups-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  @include mobile {
    grid-template-columns: 1fr;
  }
}

.lineup-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.lineup-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 10px;
  border-bottom: 2px solid $gray-200;
}

.lineup-logo {
  width: 36px;
  height: 36px;
  object-fit: contain;
}

.lineup-team-name {
  font-weight: 700;
  font-size: 0.95rem;
  color: $gray-800;
}

.lineup-formation {
  font-size: 0.75rem;
  color: $gray-500;
}

.lineup-coach {
  font-size: 0.75rem;
  color: $gray-500;
  margin-top: 2px;
}

.lineup-section {
  h4 {
    font-size: 0.78rem;
    color: $gray-500;
    margin: 0 0 6px;
    padding-bottom: 4px;
    border-bottom: 1px solid $gray-100;
  }
}

.player-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: $gray-100;
  }

  &.bench {
    opacity: 0.7;
  }
}

.player-number {
  width: 24px;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: $gray-400;
  flex-shrink: 0;
}

.player-name {
  flex: 1;
  font-size: 0.85rem;
  color: $gray-800;
}

.player-pos {
  font-size: 0.68rem;
  color: $gray-400;
  background: $gray-100;
  padding: 2px 6px;
  border-radius: 4px;
  flex-shrink: 0;
}

// ===== اخبار =====
.news-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  @include mobile {
    grid-template-columns: 1fr;
  }
}

.news-card {
  border-radius: 10px;
  overflow: hidden;
  background: $gray-50;
  cursor: pointer;
  border: 1px solid $gray-200;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: $shadow-md;
  }
}

.news-img {
  position: relative;
  height: 120px;
  background: $gray-200;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.news-no-img {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: $gray-400;
  font-size: 2rem;
}

.news-source-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 0.65rem;
  padding: 2px 8px;
  border-radius: 10px;
}

.news-body {
  padding: 10px 12px;

  h4 {
    font-size: 0.82rem;
    color: $gray-800;
    line-height: 1.5;
    margin: 0 0 4px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}

.news-time {
  font-size: 0.7rem;
  color: $gray-400;
}

// ===== loading/error =====
.state-box {
  text-align: center;
  padding: 100px 20px;
  color: $gray-400;

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid $gray-200;
    border-top-color: $primary-blue;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto 16px;
  }

  &.error {
    color: $red;
  }

  i {
    font-size: 3rem;
    display: block;
    margin-bottom: 12px;
  }
}

.back-btn {
  display: inline-block;
  margin-top: 16px;
  padding: 9px 22px;
  background: $primary-blue;
  color: #fff;
  border-radius: 8px;
  text-decoration: none;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// ===== آمار و رو در رو =====
.match-stats {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 14px;
}

.motm-card {
  background: linear-gradient(135deg, rgba(245, 158, 11, .12), rgba(245, 158, 11, .04));
  border: 1px solid rgba(245, 158, 11, .35);
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: transform .15s;

  &:hover { transform: translateY(-1px); }
}

.motm-badge {
  font-size: 12px;
  font-weight: 800;
  color: #b45309;
  white-space: nowrap;

  i { color: #f59e0b; margin-left: 4px; }
}

.motm-player {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
}

.motm-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;

  img { width: 100%; height: 100%; object-fit: cover; }

  &.initials {
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(245, 158, 11, .18);
    color: #b45309;
    font-weight: 800;
    font-size: 12px;
  }
}

.motm-meta {
  min-width: 0;
}

.motm-name {
  display: block;
  font-weight: 800;
  font-size: 13.5px;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.motm-sub {
  color: #b45309;
  font-size: 11.5px;
}

.motm-note {
  margin-right: auto;
  color: #9ca3af;
  font-size: 10.5px;
  white-space: nowrap;
}

.ms-bars {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ms-row {
  display: grid;
  grid-template-columns: 34px 1fr 34px;
  align-items: center;
  gap: 10px;
}

.ms-val {
  text-align: center;
  font-weight: 800;
  font-size: 14px;
  color: #374151;
}

.ms-mid {
  min-width: 0;
}

.ms-label {
  display: block;
  text-align: center;
  font-size: 11.5px;
  color: #6b7280;
  margin-bottom: 4px;
}

.ms-track {
  display: flex;
  gap: 4px;
  direction: ltr;
}

.ms-half {
  flex: 1;
  height: 8px;
  border-radius: 6px;
  background: $gray-100;
  overflow: hidden;
  display: flex;

  &.home { justify-content: flex-end; }
}

.ms-fill {
  height: 100%;
  border-radius: 6px;
  transition: width .4s ease;

  &.home { background: #10b981; }
  &.away { background: #3b82f6; }
}

.teams-overview {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.team-overview {
  background: $gray-100;
  border-radius: 12px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.overview-head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.overview-logo {
  width: 44px;
  height: 44px;
  object-fit: contain;
}

.overview-titles {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.overview-name {
  font-weight: 700;
  font-size: 0.9rem;
  color: $gray-800;
}

.overview-rank {
  font-size: 0.72rem;
  color: $secondary-blue;
  font-weight: 600;
}

.form-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.form-label {
  font-size: 0.72rem;
  color: $gray-500;
}

.form-badge {
  font-size: 0.68rem;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 8px;

  &.win {
    background: rgba(16, 185, 129, .15);
    color: #059669;
  }

  &.draw {
    background: $gray-200;
    color: $gray-600;
  }

  &.loss {
    background: rgba(239, 68, 68, .12);
    color: #dc2626;
  }
}

.overview-table {
  width: 100%;
  font-size: 0.75rem;
  border-collapse: collapse;

  td {
    padding: 5px 2px;
    border-bottom: 1px dashed $gray-200;

    &:first-child {
      color: $gray-500;
      text-align: right;
    }

    &:last-child {
      font-weight: 700;
      color: $gray-800;
      text-align: left;
    }
  }

  tr:last-child td {
    border-bottom: none;
  }
}

.h2h-box {
  margin-top: 16px;
}

.h2h-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: $gray-700;
  margin: 0 0 10px;
}

.h2h-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 10px;
  background: $gray-100;
  border-radius: 10px;
  padding: 9px 12px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: .2s;

  &:hover {
    background: $gray-200;
  }
}

.h2h-team {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  color: $gray-700;
  min-width: 0;
  cursor: pointer;

  &:hover span {
    text-decoration: underline;
  }

  img {
    width: 24px;
    height: 24px;
    object-fit: contain;
    flex-shrink: 0;
  }

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &.winner {
      font-weight: 800;
      color: $gray-900;
    }
  }

  &.away {
    flex-direction: row-reverse;
    text-align: left;
  }
}

.h2h-center {
  text-align: center;

  strong {
    display: block;
    font-size: 0.85rem;
    color: $gray-900;
  }

  small {
    font-size: 0.65rem;
    color: $gray-500;
  }
}

@media (max-width: 640px) {
  .teams-overview {
    grid-template-columns: 1fr;
  }
}
</style>